import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTelephonyStatus, telephonyApiUrl } from '../utils/telephony';
import { useSoftphone } from './softphone/SoftphoneContext';

const POLL_MS = 4000;

/**
 * Incoming-call pop-up. Polls the authenticated /api/telephony/live
 * endpoint while the Netgsm integration is enabled (caller data is not sent
 * over the unauthenticated notification websocket). Shows the matched
 * customer / contact / lead with a link to its record.
 */
export default function IncomingCallWatcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const dismissed = useRef(new Set());
  const softphone = useSoftphone();

  useEffect(() => {
    let alive = true;
    let timer = null;

    const poll = async () => {
      try {
        const res = await axios.get(`${telephonyApiUrl}/api/telephony/live`);
        if (alive) setCalls((res.data || []).filter((c) => !dismissed.current.has(c.id)));
      } catch (e) {
        // transient — try again next tick
      }
      if (alive) timer = setTimeout(poll, POLL_MS);
    };

    getTelephonyStatus().then((s) => {
      if (alive && s.enabled) poll();
    });
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // The in-browser phone shows (and answers) incoming calls itself.
  if (!calls.length || (softphone && softphone.status !== 'off')) return null;

  const dismiss = (id) => {
    dismissed.current.add(id);
    setCalls((cs) => cs.filter((c) => c.id !== id));
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-2 w-80 max-w-[calc(100vw-2rem)]">
      {calls.map((c) => (
        <div key={c.id} className="rounded-lg shadow-lg border border-emerald-300 bg-white dark:bg-gray-800 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 animate-pulse">📞 {t('shell.softphone.incomingCall')}</div>
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {c.party_name || t('shell.softphone.unknownNumber')}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{c.customer_num}</div>
            </div>
            <button
              type="button"
              onClick={() => dismiss(c.id)}
              className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          {c.party_url && (
            <button
              type="button"
              onClick={() => {
                dismiss(c.id);
                navigate(c.party_url);
              }}
              className="mt-2 text-sm text-blue-700 hover:underline"
            >
              {t('shell.softphone.openRecord')} →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
